import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUndoAlt, FaSearch, FaUser, FaClock, FaCheck, FaTimes, FaExclamationTriangle, FaTrashAlt, FaBars } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUserRole } from '../../../utils/roleUtils';
import { getApiEndpoint } from '../../../config/api';
import CloudStaffSidebar from './CloudStaffSidebar';

const AccountRecovery = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recovering, setRecovering] = useState(null);
  const [search, setSearch] = useState('');
  const [confirmId, setConfirmId] = useState(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useEffect(() => {
    const role = getUserRole();
    if (role !== 'cloud_staff' && role !== 'super_admin') {
      toast.error('Access denied');
      navigate('/login');
      return;
    }
    fetchDeletedAccounts();
  }, [navigate]);

  const fetchDeletedAccounts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(getApiEndpoint('/api/cloud-staff/deleted-accounts'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAccounts(data.accounts || []);
      } else {
        toast.error('Failed to load deleted accounts');
      }
    } catch {
      toast.error('Network error loading accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleRecover = async (userId) => {
    setRecovering(userId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(getApiEndpoint(`/api/cloud-staff/recover-account/${userId}`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Account recovered successfully');
        setAccounts(prev => prev.filter(a => a.id !== userId));
        setConfirmId(null);
      } else {
        toast.error(data.message || 'Failed to recover account');
      }
    } catch {
      toast.error('Network error during recovery');
    } finally {
      setRecovering(null);
    }
  };

  const filtered = accounts.filter(a => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (a.fullName || '').toLowerCase().includes(q) ||
      (a.email || '').toLowerCase().includes(q) ||
      (a.role || '').toLowerCase().includes(q) ||
      (a.companyName || '').toLowerCase().includes(q)
    );
  });

  const formatDate = (d) => {
    if (!d) return 'N/A';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatRole = (role) => {
    const map = {
      staff: 'Staff', contractor: 'Contractor', building_manager: 'Building Manager',
      cloud_staff: 'CloudSafe Staff', fm: 'Facilities Manager', admin: 'Admin',
      super_admin: 'Super Admin', viewer: 'Viewer', owner: 'Property Owner',
    };
    return map[role] || (role || '').replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  return (
    <div className="cs-dashboard">
      <header className="cs-header">
        <div className="cs-header-inner">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="cs-mobile-menu-btn" onClick={() => setMobileSidebarOpen(true)}>
              <FaBars />
            </button>
            <h2 style={{ color: 'var(--text-header)', fontSize: '1rem', fontWeight: 700, margin: 0, fontFamily: 'var(--ui)' }}>
              <FaUndoAlt style={{ color: 'var(--primary)', marginRight: 8 }} />Account Recovery
            </h2>
          </div>
        </div>
      </header>

      <div className="cs-layout">
      <CloudStaffSidebar active="account-recovery" mobileOpen={mobileSidebarOpen} onClose={() => setMobileSidebarOpen(false)} />
      <main className="cs-content">
        <div className="cs-page-header" style={{ marginBottom: 24 }}>
          <h1 style={{ color: 'var(--text-header)', fontFamily: 'var(--ui)', fontSize: '1.5rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
            <FaUndoAlt style={{ color: 'var(--primary)' }} /> Account Recovery
          </h1>
          <p style={{ color: 'var(--text)', fontSize: '0.85rem', marginTop: 4 }}>
            Deleted accounts within the 30-day recovery window. Reinstate accounts so users can log back in.
          </p>
        </div>

        {/* Search */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20,
          background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 10,
          padding: '10px 16px', maxWidth: 460,
        }}>
          <FaSearch style={{ color: 'var(--text-2)', flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Search by name, email, role, or company..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              flex: 1, background: 'none', border: 'none', outline: 'none',
              color: 'var(--text-bright)', fontSize: '0.85rem', fontFamily: 'var(--ui)',
            }}
          />
        </div>

        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-2)' }}>
            <div className="cs-spinner" style={{ margin: '0 auto 16px' }} />
            Loading deleted accounts...
          </div>
        ) : filtered.length === 0 ? (
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border-dim)', borderRadius: 14,
            padding: '48px 24px', textAlign: 'center',
          }}>
            <FaTrashAlt style={{ fontSize: 32, color: 'var(--text-2)', marginBottom: 12 }} />
            <p style={{ color: 'var(--text)', fontSize: '0.95rem', fontWeight: 600 }}>
              {search ? 'No matching deleted accounts' : 'No deleted accounts within recovery window'}
            </p>
            <p style={{ color: 'var(--text-2)', fontSize: '0.8rem', marginTop: 6 }}>
              {search ? 'Try a different search term.' : 'Accounts are only recoverable within 30 days of deletion.'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {filtered.map(account => (
              <div key={account.id} style={{
                background: 'var(--surface)', border: '1px solid var(--border-dim)', borderRadius: 14,
                padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 16,
                transition: 'border-color 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border-hover)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-dim)'}
              >
                {/* Avatar */}
                <div style={{
                  width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                  background: account.canRecover ? 'var(--primary-dim)' : 'var(--danger-dim)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: account.canRecover ? 'var(--primary)' : 'var(--danger)',
                  fontSize: 18,
                }}>
                  <FaUser />
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ color: 'var(--text-bright)', fontWeight: 600, fontSize: '0.95rem' }}>
                      {account.fullName || 'Unknown'}
                    </span>
                    <span style={{
                      background: 'var(--accent-dim)', color: 'var(--accent)', fontSize: '0.65rem',
                      fontWeight: 600, padding: '2px 8px', borderRadius: 6, textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}>
                      {formatRole(account.role)}
                    </span>
                  </div>
                  <div style={{ color: 'var(--text)', fontSize: '0.8rem', marginTop: 3 }}>
                    {account.email}
                    {account.companyName && (
                      <span style={{ color: 'var(--text-2)', marginLeft: 8 }}>
                        &middot; {account.companyName}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: '0.75rem' }}>
                    <span style={{ color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <FaTrashAlt style={{ fontSize: 10 }} /> Deleted {formatDate(account.deletedAt)}
                    </span>
                    {account.canRecover ? (
                      <span style={{ color: 'var(--warning)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <FaClock style={{ fontSize: 10 }} /> {account.daysRemaining} day{account.daysRemaining !== 1 ? 's' : ''} remaining
                      </span>
                    ) : (
                      <span style={{ color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <FaTimes style={{ fontSize: 10 }} /> Recovery expired
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div style={{ flexShrink: 0 }}>
                  {account.canRecover ? (
                    confirmId === account.id ? (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ color: 'var(--warning)', fontSize: '0.75rem', fontWeight: 500 }}>Confirm?</span>
                        <button
                          onClick={() => handleRecover(account.id)}
                          disabled={recovering === account.id}
                          style={{
                            background: 'var(--success)', color: '#fff', border: 'none', borderRadius: 8,
                            padding: '8px 14px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                            fontFamily: 'var(--ui)', display: 'flex', alignItems: 'center', gap: 5,
                            opacity: recovering === account.id ? 0.6 : 1,
                          }}
                        >
                          <FaCheck style={{ fontSize: 11 }} />
                          {recovering === account.id ? 'Recovering...' : 'Yes, Restore'}
                        </button>
                        <button
                          onClick={() => setConfirmId(null)}
                          style={{
                            background: 'var(--surface-3)', color: 'var(--text)', border: '1px solid var(--border-dim)',
                            borderRadius: 8, padding: '8px 12px', fontSize: '0.78rem', cursor: 'pointer',
                            fontFamily: 'var(--ui)',
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmId(account.id)}
                        style={{
                          background: 'var(--primary-dim)', color: 'var(--primary)', border: '1px solid var(--border)',
                          borderRadius: 10, padding: '9px 18px', fontSize: '0.8rem', fontWeight: 600,
                          cursor: 'pointer', fontFamily: 'var(--ui)', display: 'flex', alignItems: 'center', gap: 6,
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--primary)'; e.currentTarget.style.color = '#fff'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'var(--primary-dim)'; e.currentTarget.style.color = 'var(--primary)'; }}
                      >
                        <FaUndoAlt style={{ fontSize: 12 }} /> Reinstate
                      </button>
                    )
                  ) : (
                    <span style={{
                      background: 'var(--danger-dim)', color: 'var(--danger)', fontSize: '0.72rem',
                      fontWeight: 600, padding: '6px 12px', borderRadius: 8,
                    }}>
                      <FaExclamationTriangle style={{ marginRight: 4, fontSize: 10 }} />
                      Expired
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      </div>
    </div>
  );
};

export default AccountRecovery;
